import React, { useState, useEffect } from "react";
import { Copy, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import SearchBar from "../common/SearchBar";
import { PrimaryButton } from "../common/ActionButtons";
import n8nConfig from "../../config/n8n.config";

const CloneWorkflow = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cloning, setCloning] = useState(null);
  const [clonedWorkflows, setClonedWorkflows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "http://localhost:3001/api/n8n/proxy/workflows",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            baseUrl: n8nConfig.baseUrl,
            apiKey: n8nConfig.apiKey,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflows(data.data || []);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      setError(error.message);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const cloneWorkflow = async (workflow) => {
    setCloning(workflow.id);
    setError(null);

    try {
      // Step 1: Get full workflow details (exclude pinned data for cleaner clone)
      const workflowResponse = await fetch(
        `http://localhost:3001/api/n8n/proxy/workflow/${workflow.id}?excludePinnedData=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            baseUrl: n8nConfig.baseUrl,
            apiKey: n8nConfig.apiKey,
          }),
        },
      );

      if (!workflowResponse.ok) {
        throw new Error(
          `Failed to fetch workflow details: ${workflowResponse.statusText}`,
        );
      }

      const workflowData = await workflowResponse.json();

      // Handle both response formats: { data: {...} } or {...} directly
      const workflow_details = workflowData.data || workflowData;

      if (!workflow_details || !workflow_details.name) {
        console.error("Unexpected workflow response:", workflowData);
        throw new Error("Invalid workflow data received from API");
      }

      // Step 2: Create clone with FULL workflow data (n8n API needs everything)
      // Don't clean nodes - send everything as-is
      const newWorkflow = {
        name: `${workflow_details.name} (Copy)`,
        nodes: workflow_details.nodes || [],
        connections: workflow_details.connections || {},
        settings: workflow_details.settings || {},
        staticData: workflow_details.staticData || null,
      };

      // Add optional properties if present
      if (workflow_details.tags && workflow_details.tags.length > 0) {
        newWorkflow.tags = workflow_details.tags;
      }

      // Note: Don't include id, versionId, createdAt, updatedAt, shared
      // n8n will assign these

      console.log("Creating clone with full workflow data:", {
        name: newWorkflow.name,
        nodeCount: newWorkflow.nodes.length,
        connectionCount: Object.keys(newWorkflow.connections).length,
        hasSettings: !!newWorkflow.settings,
        hasStaticData: !!newWorkflow.staticData,
      });

      const cloneResponse = await fetch(
        "http://localhost:3001/api/n8n/proxy/workflows/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            baseUrl: n8nConfig.baseUrl,
            apiKey: n8nConfig.apiKey,
            workflow: newWorkflow,
          }),
        },
      );

      if (!cloneResponse.ok) {
        const errorText = await cloneResponse.text();
        throw new Error(`Failed to create workflow clone: ${errorText}`);
      }

      const clonedData = await cloneResponse.json();
      const cloned_workflow = clonedData.data || clonedData;

      console.log("Successfully cloned workflow:", cloned_workflow);

      // Show success message
      setError(`✅ Successfully cloned "${workflow.name}"`);

      // Track cloned workflow
      setClonedWorkflows((prev) => [...prev, workflow.id]);

      // Clear success message and refresh after 3 seconds
      setTimeout(() => {
        setError(null);
        fetchWorkflows();
      }, 3000);
    } catch (error) {
      console.error("Error cloning workflow:", error);
      setError(`Clone failed: ${error.message}`);
    } finally {
      setCloning(null);
    }
  };

  const filteredWorkflows = workflows.filter(
    (workflow) =>
      workflow.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.id?.toString().includes(searchTerm),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Clone Workflow</h1>
          <p className="text-slate-600">
            Duplicate existing workflows with one click
          </p>
        </div>
        <PrimaryButton
          onClick={fetchWorkflows}
          icon={RefreshCw}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </PrimaryButton>
      </div>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onRefresh={fetchWorkflows}
        placeholder="Search workflows to clone..."
      />

      {error && (
        <div
          className={`${error.startsWith("✅") ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border rounded-lg p-4 flex items-start gap-3`}
        >
          {error.startsWith("✅") ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h3
              className={`text-sm font-semibold ${error.startsWith("✅") ? "text-green-900" : "text-red-900"}`}
            >
              {error.startsWith("✅") ? "Success" : "Error"}
            </h3>
            <p
              className={`text-sm ${error.startsWith("✅") ? "text-green-800" : "text-red-800"} mt-1`}
            >
              {error}
            </p>
            {!error.startsWith("✅") && (
              <p className="text-xs text-red-700 mt-2">
                Check that your n8n URL and API key are correct in{" "}
                <code className="bg-red-100 px-1 rounded">
                  src/config/n8n.config.js
                </code>
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Nodes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                </td>
              </tr>
            ) : filteredWorkflows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No workflows found. Connect to your n8n instance to see
                  workflows.
                </td>
              </tr>
            ) : (
              filteredWorkflows.map((workflow) => {
                const isCloning = cloning === workflow.id;
                const wasCloned = clonedWorkflows.includes(workflow.id);

                return (
                  <tr key={workflow.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {workflow.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      {workflow.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          workflow.active
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {workflow.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {workflow.nodes?.length || 0} nodes
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        {wasCloned && !isCloning && (
                          <div className="flex items-center text-green-600 text-xs mr-2">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Cloned!
                          </div>
                        )}
                        <button
                          onClick={() => cloneWorkflow(workflow)}
                          disabled={isCloning}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            isCloning
                              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                        >
                          {isCloning ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Cloning...
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              Clone
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            ℹ️ How It Works
          </h3>
          <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
            <li>Click "Clone" on any workflow</li>
            <li>A copy will be created with "(Copy)" appended to the name</li>
            <li>The cloned workflow will be inactive by default</li>
            <li>All nodes and connections are preserved</li>
            <li>Edit the cloned workflow in n8n to customize it</li>
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">
            ⚙️ Configuration Required
          </h3>
          <ul className="text-sm text-amber-800 space-y-1 ml-4 list-disc">
            <li>Get your n8n API key from Settings → API</li>
            <li>Update the API base URL in cloneWorkflow()</li>
            <li>Add your API key to the headers</li>
            <li>Uncomment the API calls</li>
            <li>Test with a simple workflow first</li>
          </ul>
        </div>
      </div>

      {clonedWorkflows.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-green-900 mb-2">
            ✅ Successfully Cloned ({clonedWorkflows.length})
          </h3>
          <p className="text-sm text-green-800">
            {clonedWorkflows.length} workflow
            {clonedWorkflows.length > 1 ? "s have" : " has"} been cloned. Check
            your n8n workflows list to see the new copies.
          </p>
        </div>
      )}
    </div>
  );
};

export default CloneWorkflow;
